import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import AuthSplitLayout from '@layouts/AuthSplitLayout/AuthSplitLayout';
import Button from '@components/Button/Button';
import Input from '@components/Form/Input/Input';
import Checkbox from '@components/Form/Checkbox/Checkbox';
import Icon from '@components/common/Icon';
import { authService } from '@/services/auth/authService';
import { useAuth } from '@context/AuthContext';

interface SignInFormValues {
    email: string;
    password: string;
    rememberMe: boolean;
}

const SignInPage = () => {
    const { t } = useTranslation('auth');
    const navigate = useNavigate();
    const { login } = useAuth();

    const [showPassword, setShowPassword] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const { register, handleSubmit, formState: { errors } } =
        useForm<SignInFormValues>({ defaultValues: { rememberMe: false } });

    const onLogin = async (data: SignInFormValues) => {
        setIsSubmitting(true);
        try {
            const response = await authService.login({
                email: data.email?.trim() || '',
                password: data.password?.trim() || '',
            });
            await login(response.data.data.access_token);
            toast.success(response.data.message);
            navigate('/admin/cohorts', { replace: true });
        } catch (err: unknown) {
            const error = err as { response?: { data?: { message?: string } } };
            const message = error?.response?.data?.message || 'Login failed. Please try again.';
            toast.error(message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const EyeToggle = ({ show, onToggle }: { show: boolean; onToggle: () => void }) => (
        <button type="button" onClick={onToggle}
            aria-label={show ? 'Hide password' : 'Show password'}
            className="flex items-center">
            <Icon name={show ? 'eyeHide' : 'eyeShow'} className="w-[12px] 2xl:w-[14px] h-auto" />
        </button>
    );

    return (
        <AuthSplitLayout>
            <div className="w-full max-w-[400px] bg-white rounded-xl shadow-[var(--shadow-lg)] border border-[var(--neutral-color)] p-[20px] 2xl:p-[30px] flex flex-col">
                <h1 className="text-[20px] 2xl:text-[28px] font-semibold text-[var(--secondary-color)] leading-tight mb-[20px] 2xl:mb-[30px]">
                    {t('sign_in.title')}
                </h1>
                <form onSubmit={handleSubmit(onLogin)} noValidate>
                    <Input
                        label={t('sign_in.email_label')}
                        type="email"
                        placeholder={t('sign_in.email_placeholder')}
                        required
                        registration={register('email', {
                            required: t('sign_in.email_required'),
                            pattern: {
                                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                message: 'Please enter a valid email address',
                            },
                        })}
                        error={errors.email?.message}
                    />

                    <Input
                        label={t('sign_in.password_label', { defaultValue: 'Password' })}
                        type={showPassword ? 'text' : 'password'}
                        placeholder={t('sign_in.password_placeholder', { defaultValue: 'Enter your password' })}
                        required
                        rightElement={<EyeToggle show={showPassword} onToggle={() => setShowPassword(v => !v)} />}
                        registration={register('password', {
                            required: t('sign_in.password_required', { defaultValue: 'Password is required' }),
                            minLength: {
                                value: 6,
                                message: t('sign_in.password_min_length', { defaultValue: 'Minimum 6 characters' }),
                            },
                        })}
                        error={errors.password?.message}
                    />

                    <div className="flex items-center justify-between py-2 mb-[20px]">
                        <Checkbox
                            label={t('sign_in.remember_me')}
                            registration={register('rememberMe')}
                        />
                    </div>

                    <Button type="submit" variant="primary" fullWidth disabled={isSubmitting}>
                        {isSubmitting ? t('sign_in.logging_in_button', { defaultValue: 'Signing in...' }) : t('sign_in.login_button')}
                    </Button>

                    <p className="text-center text-[12px] 2xl:text-[14px] text-[var(--subtitle-color)] font-medium mt-[20px]">
                        {t('sign_in.need_account')}{' '}
                        <span className="text-[var(--primary-color)] font-medium cursor-pointer hover:underline">
                            {t('sign_in.contact_admin')}
                        </span>
                    </p>
                </form>
            </div>
        </AuthSplitLayout>
    );
};

export default SignInPage;
